import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import HeaderOne from "../components/HeaderOne";
import FooterOne from "../components/FooterOne";
import Modal from "../components/Modal";
import EbookForm from "../components/EbookForm";
import GetDemoForm from "../components/GetDemoForm";
import CalendlyModal from "../components/CalendlyModal";
import LoginModalForm from "../components/LoginModalForm";
import RegisterModalForm from "../components/RegisterModalForm";
import VerifyEmailModalForm from "../components/VerifyEmailModalForm";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import ChangeEmailModalForm from "../components/ChangeEmailModalForm";
import PartnerApplicationModal from "../components/PartnerApplicationModal";
import ClientPlatformSetupWizard from "../components/ClientPlatformSetupWizard";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import TerminalDetails from "../components/TerminalDetails";

const MasterLayout = ({
  openEbookModal,
  setOpenEbookModal,
  openGetDemoModal,
  setOpenGetDemoModal,
  openCalendlyModal,
  setOpenCalendlyModal,
  openLoginModal,
  setOpenLoginModal,
  loginModalTitle,
  openRegisterModal,
  setOpenRegisterModal,
  openVerifyEmailModal,
  pendingVerificationEmail,
  setPendingVerificationEmail,
  openForgotPasswordModal,
  openChangeEmailModal,
  changeEmailContext,
  setChangeEmailContext,
  openPartnerModal,
  openClientSetupModal,
  clientSetupContext,
  closeModal, // from useModals
  openModal,
  isModalOpen,
  handleCloseModal,
  selectedCard,
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const goToVerifyAfterRegister = (email) => {
    closeModal("register");
    setPendingVerificationEmail(email || "");
    openModal("verifyEmail");
  };

  const goToLoginAfterVerify = () => {
    closeModal("verifyEmail");
    setPendingVerificationEmail("");
    const sp = new URLSearchParams(location.search);
    sp.set("registered", "1");
    navigate(
      {
        pathname: location.pathname,
        hash: location.hash,
        search: `?${sp.toString()}`,
      },
      { replace: true },
    );
    openModal("login");
  };

  const goToRegisterFromVerify = () => {
    closeModal("verifyEmail");
    setPendingVerificationEmail("");
    openModal("register");
  };

  const goToLoginFromVerify = () => {
    closeModal("verifyEmail");
    setPendingVerificationEmail("");
    openModal("login");
  };

  const goToVerifyFromLogin = (email) => {
    closeModal("login");
    setPendingVerificationEmail(email || "");
    openModal("verifyEmail");
  };

  const goToLoginAfterForgot = () => {
    closeModal("forgotPassword");
    const sp = new URLSearchParams(location.search);
    sp.set("passwordChanged", "1");
    navigate(
      {
        pathname: location.pathname,
        hash: location.hash,
        search: `?${sp.toString()}`,
      },
      { replace: true },
    );
    openModal("login");
  };
  const orgHy = {
    "@type": "Organization",
    "@id": "https://nomio.am/#organization",
    name: "ITResources",
    url: "https://nomio.am",
    logo: "https://nomio.am/logo.png",
    sameAs: [
      "https://www.facebook.com/ITResources.am",
      "https://www.linkedin.com/company/itresources-armenia",
    ],
    description: "Nomio-ն ժամանակակից որակի կառավարման համակարգ է (QMS), որը հնարավորություն է տալիս սահմանել, վերահսկել, վերլուծել և բարելավել սպասարկման որակը։",
    email: "info@nomio.am",
    address: {
      "@type": "PostalAddress",
      addressCountry: "AM",
      addressLocality: "Երևան",
      addressRegion: "Երևան",
      postalCode: "0001",
      streetAddress: "Խանջյան 67",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: "+374 41 99 60 08",
      areaServed: "AM",
    },
  };

  const orgEn = {
    ...orgHy,
    description: "Nomio is a modern Quality Management System (QMS) that helps organizations define, monitor, analyze, and continuously improve service quality through data-driven insights.",
    address: {
      "@type": "PostalAddress",
      addressCountry: "AM",
      addressLocality: "Yerevan",
      addressRegion: "Yerevan",
      postalCode: "0001",
      streetAddress: "67 Khanjyan St",
    },
  };

  const productHy = {
    "@type": "Product",
    "@id": "https://nomio.am/#product",
    name: "Nomio",
    url: "https://nomio.am",
    image: "https://nomio.am/assets/og-image.jpg",
    description: "Nomio-ն ամբողջական որակի կառավարման հարթակ է, որն օգնում է կազմակերպություններին վերահսկել հերթերը, գնահատել աշխատակիցների արդյունավետությունը և բարելավել հաճախորդների սպասարկումը՝ տվյալահեն որոշումների միջոցով։",
    brand: { "@type": "Brand", name: "Nomio" },
    manufacturer: { "@id": "https://nomio.am/#organization" },
    offers: {
      "@type": "Offer",
      priceCurrency: "AMD",
      availability: "https://schema.org/InStock",
    },
  };

  const productEn = {
    ...productHy,
    name: "Nomio",
    description: "Nomio is an all-in-one QMS platform designed to help businesses manage queues, monitor employee performance, and enhance customer experience through data analytics and automation.",
  };

  const globalSchema = {
    "@context": "https://schema.org",
    "@graph": [
      i18n.language === "hy" ? orgHy : orgEn,
      i18n.language === "hy" ? productHy : productEn,
      {
        "@type": "WebSite",
        "@id": "https://nomio.am/#website",
        url: "https://nomio.am/",
        name: "Nomio",
        inLanguage: i18n.language,
        publisher: { "@id": "https://nomio.am/#organization" },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <meta name="keywords" content={t("meta.keywords")} />

        <script type="application/ld+json">
          {JSON.stringify(globalSchema)}
        </script>
      </Helmet>

      {/* Header */}
      <HeaderOne
        setOpenGetDemoModal={setOpenGetDemoModal}
        setOpenLoginModal={setOpenLoginModal}
        setOpenRegisterModal={setOpenRegisterModal}
      />

      {/* Page Content */}
      <Outlet />

      {/* Footer */}
      <FooterOne />

      {/* Modals */}
      <Modal
        isOpen={openEbookModal}
        onClose={() => closeModal("ebook")}
        title={t("becomeSellerTwo.ebookModal.title")}
      >
        <EbookForm />
      </Modal>

      <Modal
        isOpen={openGetDemoModal}
        onClose={() => closeModal("getDemo")}
        title={t("getDemoForm.title")}
      >
        <GetDemoForm />
      </Modal>

      <Modal
        isOpen={openLoginModal}
        onClose={() => closeModal("login")}
        title={loginModalTitle || t("authModal.loginTitle")}
      >
        <LoginModalForm
          preventRedirectOnSuccess={Boolean(loginModalTitle)}
          onLoginSuccess={() => closeModal("login")}
          onSwitchToRegister={() => {
            closeModal("login");
            openModal("register");
          }}
          onForgotPassword={() => {
            closeModal("login");
            openModal("forgotPassword");
          }}
          onEmailNotVerified={goToVerifyFromLogin}
        />
      </Modal>

      <Modal
        isOpen={openRegisterModal}
        onClose={() => closeModal("register")}
        title={t("authModal.registerTitle")}
      >
        <RegisterModalForm
          onRegisterSuccess={goToVerifyAfterRegister}
          onSwitchToLogin={() => {
            closeModal("register");
            openModal("login");
          }}
        />
      </Modal>

      <Modal
        isOpen={openVerifyEmailModal}
        onClose={() => {
          closeModal("verifyEmail");
          setPendingVerificationEmail("");
        }}
        title={t("authModal.verify.title")}
      >
        <VerifyEmailModalForm
          email={pendingVerificationEmail}
          onVerifySuccess={goToLoginAfterVerify}
          onBackToRegister={goToRegisterFromVerify}
          onGoToLogin={goToLoginFromVerify}
        />
      </Modal>

      <Modal
        isOpen={openForgotPasswordModal}
        onClose={() => closeModal("forgotPassword")}
        title={t("authModal.forgot.title")}
      >
        <ForgotPasswordModal
          onSuccess={goToLoginAfterForgot}
          onSwitchToLogin={() => {
            closeModal("forgotPassword");
            openModal("login");
          }}
        />
      </Modal>

      <Modal
        isOpen={openChangeEmailModal}
        onClose={() => closeModal("changeEmail")}
        title={t("profile.changeEmail.title")}
      >
        <ChangeEmailModalForm
          currentEmail={changeEmailContext?.currentEmail || ""}
          onSuccess={() => {
            closeModal("changeEmail");
            if (typeof setChangeEmailContext === "function") {
              setChangeEmailContext({ currentEmail: "" });
            }
            // Notify ProfilePage to refetch by reloading – simplest approach.
            // ProfilePage refetches on mount and on token change; trigger a soft refresh.
            window.dispatchEvent(new Event("nomio:user-updated"));
          }}
        />
      </Modal>

      <Modal
        isOpen={openPartnerModal}
        onClose={() => closeModal("partner")}
        title={t("partner.modalTitle")}
        wide
      >
        <PartnerApplicationModal
          onSuccess={() => {
            closeModal("partner");
            window.dispatchEvent(new Event("nomio:user-updated"));
          }}
        />
      </Modal>

      <Modal
        isOpen={openClientSetupModal}
        onClose={() => closeModal("clientSetup")}
        title={t("clientSetup.modalTitle")}
        wide
      >
        <ClientPlatformSetupWizard
          initialContext={clientSetupContext || {}}
          onClose={() => closeModal("clientSetup")}
        />
      </Modal>

      <Modal
        isOpen={openCalendlyModal}
        onClose={() => closeModal("calendly")}
        large="large"
      >
        <CalendlyModal />
      </Modal>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        large="large"
        withoutLogo
      >
        <TerminalDetails selectedCard={selectedCard} />
      </Modal>
    </>
  );
};

export default MasterLayout;
